import {
  AssetListInfoItems,
  CalculatedContractStatusEnum,
  InfoItemEnum,
  InfoItems,
  InfoItemsActions,
  InfoItemsAssetMetaData,
  OperationalStatusEnum,
  SecondaryStatusEnum,
  SecondarySubStatusEnum,
  ServiceContractTypes,
} from '@uptime-bff/api-contract';
import { ExtendedSecondaryStatusRoboticMowerEnum as SecondaryStatusRoboticMowerEnum } from 'core/models/backend-translations-types';
import {
  InternationalizationBackendNameSpace,
  InternationalizationNameSpace,
} from 'core/models/internationalization-namespace';
import { displayDateFormat, displayDateTimeFormat } from 'core/styles/variables';
import i18next from 'i18next';
import { DateTime } from 'luxon';

export const OPERATING_SECONDARY_STATUS_GROUP = [
  SecondaryStatusRoboticMowerEnum.MOWING,
  SecondaryStatusRoboticMowerEnum.TRANSPORTING,
  SecondaryStatusRoboticMowerEnum.CHARGING,
  SecondaryStatusRoboticMowerEnum.PREPARING,
];

export const ERROR_SECONDARY_STATUS_GROUP = [
  SecondaryStatusRoboticMowerEnum.ERROR,
  SecondaryStatusRoboticMowerEnum.FATAL_ERROR,
];

export const countSecondaryStatusTags = (values?: string[]): number => {
  if (!values?.length) {
    return 0;
  }
  const remaining = new Set<string>(values);
  let count = 0;
  if (OPERATING_SECONDARY_STATUS_GROUP.some((s) => remaining.has(s))) {
    count += 1;
    OPERATING_SECONDARY_STATUS_GROUP.forEach((s) => remaining.delete(s));
  }
  if (ERROR_SECONDARY_STATUS_GROUP.some((s) => remaining.has(s))) {
    count += 1;
    ERROR_SECONDARY_STATUS_GROUP.forEach((s) => remaining.delete(s));
  }
  return count + remaining.size;
};

export const lengthOf = (value?: unknown[]): number => (Array.isArray(value) ? value.length : 0);

export interface ProbableCause {
  cause: string;
  action: string;
}

export type Severity = 50 | 40 | 30 | 20 | 10 | undefined;

export const formatDate = (dateString: string | undefined) =>
  (dateString ? new Date(dateString).toISOString().split('T')[0] : undefined);

export const statusBadgeColor = (status?: OperationalStatusEnum) => {
  switch (status) {
    case OperationalStatusEnum.OK:
    case OperationalStatusEnum.OK_WITH_INFO:
      return 'success';
    case OperationalStatusEnum.WARNING:
      return 'caution';
    case OperationalStatusEnum.CRITICAL:
      return 'error';
    case OperationalStatusEnum.UNKNOWN:
      return 'other';
    default:
      return undefined;
  }
};

export const getSeverityIcon = (severity: Severity) => {
  switch (severity) {
    case 50:
      return 'notification.error_outlined';
    case 40:
    case 30:
      return 'other.warning';
    case 20:
    case 10:
      return 'notification.info_outlined';
    default:
      return 'none';
  }
};

export const shouldDisplayStatusIcon = (status?: OperationalStatusEnum) =>
  status && status !== OperationalStatusEnum.OK && status !== OperationalStatusEnum.OK_WITH_INFO;

export const shouldDisplayGreenDot = (status?: OperationalStatusEnum) =>
  status && (status === OperationalStatusEnum.OK || status === OperationalStatusEnum.OK_WITH_INFO);

export const getDate = (date?: Date): string => {
  if (!date) {
    return '';
  }

  return String(DateTime.fromISO(String(date)).toFormat(displayDateFormat));
};

export const mapInfoItemTitleLanguageKey = (infoItemId?: InfoItemEnum) => {
  if (!infoItemId) {
    return '';
  }
  const textKey = `statusTranslation:status.infoItems.${infoItemId}.text`;
  const contentKey = `statusTranslation:status.infoItems.${infoItemId}.content`;
  const baseKey = `statusTranslation:status.infoItems.${infoItemId}`;

  return i18next.exists(textKey) && i18next.exists(contentKey) ? textKey : baseKey;
};

export const mapInfoItemMetaDate = (localTime?: Date, utcTime?: Date) => {
  // Fix for ISO strings with 'Z' at the end which indicates UTC time
  const correctedLocalTime = localTime && String(localTime).split('Z').join('');

  if (utcTime) {
    return DateTime.fromISO(String(utcTime), { zone: 'utc' }).toFormat(displayDateTimeFormat); // UTC TIME
  }
  if (localTime) {
    return correctedLocalTime && DateTime.fromISO(correctedLocalTime).toFormat(displayDateTimeFormat);
  }
  return undefined;
};

const mapInfoItemErrorDescription = (action?: InfoItemsActions, errorCode?: number) => {
  const langKey = `errorCodes.${action?.productIdentification}.${errorCode}.name`;
  const translation = i18next.t(`${InternationalizationBackendNameSpace.ErrorCode}:${langKey}`);

  if (translation?.toString()?.indexOf(langKey) === -1) {
    return translation;
  }

  const genericLangKey = `description.${errorCode}`;
  const genericTranslation = i18next.t(
    `${InternationalizationNameSpace.RoboticMowerCodes}:${genericLangKey}`
  );
  if (genericTranslation?.toString()?.indexOf(genericLangKey) === -1) {
    return genericTranslation;
  }

  return i18next.t(
    `${InternationalizationBackendNameSpace.Status}:status.infoItems.placeholders.MOWER_ERROR_NO_DESCRIPTION`
  );
};

export const mapSecondaryStatus = (status: SecondaryStatusEnum): string =>
  i18next.t(`${InternationalizationBackendNameSpace.Status}:status.secondaryStatus.${status}`);

export const mapSecondarySubStatus = (substatus:SecondarySubStatusEnum): string =>
  i18next.t(
    `${InternationalizationBackendNameSpace.Status}:status.secondaryStatus.additionalStatus.${substatus}`
  );

export const mapLastReportedStatus = (
  lastSecondaryStatus?: SecondaryStatusEnum,
  lastSecondarySubStatus?: SecondarySubStatusEnum
): string => {
  if (!lastSecondaryStatus) {
    return '-';
  }
  return lastSecondarySubStatus
    ? `${mapSecondaryStatus(lastSecondaryStatus)} (${mapSecondarySubStatus(lastSecondarySubStatus)})`
    : mapSecondaryStatus(lastSecondaryStatus);
};

export const mapMowerChargingNextStartDateTitle = (
  baseLangKey: string,
  utcTimestampOn: Date,
  formattedDate?: string
) => {
  const minutes = Math.floor(DateTime.fromJSDate(utcTimestampOn).diffNow('minutes').minutes);
  if (minutes >= 0 && minutes < 60) {
    return i18next.t(`${baseLangKey}.inMinutes`, {
      minutes,
    });
  }

  return i18next.t(`${baseLangKey}.atDate`, {
    formattedDate: formattedDate ?? '-',
  });
};

export const mapMowerAbandonedAreaInfoItem = (missionId?: number, assetMeta?: InfoItemsAssetMetaData[]) => {
  const item = assetMeta?.find((area) => area.listItemId === missionId);
  return {
    areaName: item?.name ?? '-',
    progress: item?.progress ?? 0,
  };
};

export const mapInfoItemErrorAndDescription = (
  action?: InfoItemsActions,
  errorCode?: number,
  formattedDate?: string
) => {
  const description = mapInfoItemErrorDescription(action, errorCode);

  if (errorCode !== 0) {
    const date = formattedDate ? ` ${formattedDate}` : '';
    return `[${errorCode}] ${description}${date}`;
  }
  return description;
};

export const mapInfoItemErrorAndDescriptionShort = (action?: InfoItemsActions, errorCode?: number) =>
  mapInfoItemErrorAndDescription(action, errorCode);

// mapping infoItem titles

export const mapInfoItemTitle = (
  action?: InfoItemsActions,
  infoItem?: InfoItems | AssetListInfoItems,
  assetMeta?: InfoItemsAssetMetaData[]
) => {
  const langKey = mapInfoItemTitleLanguageKey(infoItem?.infoItemId);
  const erroCode = infoItem?.errorCode;
  const formattedDate = mapInfoItemMetaDate(infoItem?.localTimestampOn, infoItem?.utcTimestampOn);

  switch (infoItem?.infoItemId) {
    case InfoItemEnum.MAINTENANCE_REMINDER_DUE_BY_DATE:
      return i18next.t(langKey, { formattedDate: infoItem.reminderDate ? getDate(infoItem.reminderDate) : '-' });
    case InfoItemEnum.MAINTENANCE_REMINDER_DUE_BY_HOURS:
      return i18next.t(langKey, { reminderHours: infoItem.reminderHours ??  '-' });
    case InfoItemEnum.MAINTENANCE_REMINDER_DUE_ON_DATE:
      return i18next.t(langKey, { formattedDate: infoItem.reminderDate ? getDate(infoItem.reminderDate) : '-' });
    case InfoItemEnum.MOWER_ERROR_AUTO_RESOLVABLE:
    case InfoItemEnum.MOWER_ERROR_CONFIRMABLE:
    case InfoItemEnum.MOWER_FATAL_ERROR:
      return mapInfoItemErrorAndDescription(action, erroCode, formattedDate);
    case InfoItemEnum.MOWER_WARNING_BY_MESSAGE:
      return mapInfoItemErrorAndDescriptionShort(action, erroCode);
    case InfoItemEnum.MOWER_PARKED_NEXT_START_TIME:
    case InfoItemEnum.MOWER_MANUALLY_CONTROLLED_SINCE:
    case InfoItemEnum.MOWER_TURNED_OFF:
      return i18next.t(langKey, { formattedDate });
    case InfoItemEnum.MOWER_CHARGING_NEXT_START_TIME:
      return (
        infoItem?.utcTimestampOn && mapMowerChargingNextStartDateTitle(langKey, infoItem?.utcTimestampOn, formattedDate)
      );
    case InfoItemEnum.MOWER_DISCONNECTED:
      return i18next.t(langKey, {
        formattedDate: infoItem?.utcTimestampOn ? mapInfoItemMetaDate(undefined, infoItem?.utcTimestampOn) : '-',
        lastReportedStatus:
          mapLastReportedStatus(infoItem.lastSecondaryStatus) === '-'
            ? 'UNKNOWN'
            : mapLastReportedStatus(infoItem.lastSecondaryStatus),
      });
    case InfoItemEnum.MOWER_DISCONNECTED_SINCE:
      return i18next.t(langKey, {
        formattedDate: infoItem?.utcTimestampOn ? mapInfoItemMetaDate(undefined, infoItem?.utcTimestampOn) : '-',
      });
    case InfoItemEnum.MOWER_DISCONNECTED_LAST_SECONDARY_STATUS:
      return i18next.t(langKey, {
        lastReportedStatus: infoItem?.lastSecondaryStatus ? infoItem?.lastSecondaryStatus : '-',
      });
    case InfoItemEnum.MOWER_MOW_OVERRIDE_END_TIME:
    case InfoItemEnum.MOWER_MOW_OVERRIDE_START_TIME:
      return i18next.t(langKey, {
        formattedDateOrMinus: mapInfoItemMetaDate(infoItem.localTimestampOn, infoItem.utcTimestampOn) ?? '-',
      });
    case InfoItemEnum.MOWER_CURRENTLY_MOWED_AREA:
    case InfoItemEnum.MOWER_ABANDONED_AREA:
      return i18next.t(langKey, mapMowerAbandonedAreaInfoItem(infoItem.missionId, assetMeta));
    default:
      break;
  }
  return i18next.t(langKey) === langKey ? langKey : i18next.t(langKey);
};

// mapping InfoItem content

export const getErrorProbableCauses = (
  ns: InternationalizationNameSpace,
  action?: InfoItemsActions,
  articleNumber?: string,
  errorCode?: number
) => {
  const identifier = action?.type ? `${action?.productIdentification}` : articleNumber;
  const probableCauses = i18next.t(
    `${ns}:errorCodes.${identifier}.${errorCode}.probableCause`
  ) as unknown as ProbableCause[];
  if (probableCauses instanceof Array && probableCauses.length > 0) {
    return probableCauses;
  }
  return [];
};

export const mapErrorCodeInfoItemContent = (
  ns: InternationalizationNameSpace,
  includeDescription: boolean,
  action?: InfoItemsActions,
  articleNumber?: string,
  errorCode?: number
) => {
  const identifier = action?.type ? `${action?.productIdentification}` : articleNumber;
  const lines: string[] = [];
  if (includeDescription) {
    const contentLangKey = `errorCodes.${identifier}.${errorCode}.description`;
    const translatedContent = i18next.t(`${ns}:${contentLangKey}`);
    if (translatedContent?.toString()?.indexOf(contentLangKey) === -1) {
      lines.push(translatedContent.toString());
    } else {
      const genericLangKey = `description.${errorCode}`;
      const genericTranslation = i18next.t(
        `${InternationalizationNameSpace.RoboticMowerCodes}:${genericLangKey}`
      );
      if (genericTranslation?.toString()?.indexOf(genericLangKey) === -1) {
        lines.push(genericTranslation.toString());
      }
    }
  }

  const probableCauses = getErrorProbableCauses(ns, action, articleNumber, errorCode);
  const addLinesWithPrefix = (text: string, prefix: string) => {
    const linesToAdd = text.split('\n');
    lines.push(`${i18next.t(prefix)}: ${linesToAdd.join(' ')}`);
  };
  if (probableCauses.length > 0) {
    probableCauses.forEach((item) => {
      if (lines.length > 0) {
        lines.push('');
      }
      addLinesWithPrefix(item.cause, 'ASSETS:details.infoItems.cause');
      addLinesWithPrefix(item.action, 'ASSETS:details.infoItems.action');
    });
  }
  return lines.length > 0 ? lines : undefined;
};

export const mapErrorContent = (action?: InfoItemsActions, errorCode?: number, articleNumber?: string) =>
  mapErrorCodeInfoItemContent(InternationalizationNameSpace.ErrorCode, false, action, articleNumber, errorCode);

export const mapInfoItemContentLangKey = (infoItemId: InfoItemEnum) =>
  `statusTranslation:status.infoItems.${infoItemId}.content`;

export const mapFotaVersionDependentContent = (id: InfoItemEnum, fotaVersion?: string) => {
  const base = `statusTranslation:status.infoItems.${id}.content`;
  const fotaVersionInfo = i18next.t(`statusTranslation:status.infoItems.${id}.content.versionInfo`, {
    version: fotaVersion,
  });
  const content = i18next.t(`${base}.text`);
  return !fotaVersion ? content : [content, fotaVersionInfo].join(' ');
};

export const getInfoItemContent = (infoItem: InfoItems, articleNumber?: string, action?: InfoItemsActions) => {
  const { infoItemId, fotaVersion, errorCode } = infoItem;
  switch (infoItemId) {
    case InfoItemEnum.MOWER_ERROR_AUTO_RESOLVABLE:
    case InfoItemEnum.MOWER_ERROR_CONFIRMABLE:
    case InfoItemEnum.MOWER_FATAL_ERROR:
    case InfoItemEnum.MOWER_WARNING_BY_MESSAGE:
      if (errorCode) {
        return mapErrorContent(action, errorCode, articleNumber);
      }
      break;
    case InfoItemEnum.BATTERY_HEALTH_DEGRADED:
    case InfoItemEnum.BATTERY_HEALTH_DAMAGED:
    case InfoItemEnum.MOWER_MODE_MANUAL:
    case InfoItemEnum.NO_SENSOR_MOUNTED:
    case InfoItemEnum.NO_CONNECTIVITY_DEVICE_MOUNTED:
    case InfoItemEnum.MOWER_EPOS_WORK_AREA_NOT_IDENTIFIED:
    case InfoItemEnum.MOWER_ABANDONED_AREA:
    case InfoItemEnum.MOWER_ACTION_REQUIRED_BUTTON_RELEASE:
    case InfoItemEnum.MOWER_RESTRICTED_REASON_SENSOR:
    case InfoItemEnum.MOWER_RESTRICTED_REASON_ALL_MISSIONS_COMPLETED:
    case InfoItemEnum.MOWER_ACTION_REQUIRED_ENTER_PIN:
    case InfoItemEnum.MOWER_CHARGING_NEXT_START_TIME:
    case InfoItemEnum.MOWER_PARKED_NEXT_START_TIME:
    case InfoItemEnum.MOWER_MOW_OVERRIDE_END_TIME:
    case InfoItemEnum.MOWER_MOW_OVERRIDE_START_TIME:
    case InfoItemEnum.MOWER_GEOFENCE_DISABLED:
    case InfoItemEnum.MOWER_FOTA_FIRMWARE_UPDATE_REQUIRED:
    case InfoItemEnum.MOWER_EPOS_INSTALLATION_INCOMPLETE:
    case InfoItemEnum.MOWER_LONA_COLLECTING_MAP_DATA:
    case InfoItemEnum.MOWER_MODE_POI:
    case InfoItemEnum.MOWER_MODE_HOME:
    case InfoItemEnum.MOWER_AUTO_PAIRING_REQUIRED:
    case InfoItemEnum.MOWER_ACTION_REQUIRED_ENTER_PIN_CONFIGURED:
    case InfoItemEnum.MOWER_THEFT_GEOFENCE:
    case InfoItemEnum.MOWER_THEFT_MOTION:
    case InfoItemEnum.MOWER_THEFT_STOP_BUTTON:
      return i18next.t(mapInfoItemContentLangKey(infoItemId));
    case InfoItemEnum.MOWER_FOTA_APPROVAL_PENDING:
    case InfoItemEnum.MOWER_CS_FOTA_APPROVAL_PENDING:
    case InfoItemEnum.MOWER_FOTA_INSTALLATION_PENDING:
    case InfoItemEnum.MOWER_CS_FOTA_INSTALLATION_PENDING:
    case InfoItemEnum.MOWER_FOTA_INSTALLING:
    case InfoItemEnum.MOWER_CS_FOTA_INSTALLING:
      return mapFotaVersionDependentContent(infoItemId, fotaVersion);
    default:
      break;
  }
  return null;
};

/**
 * Reorders a list so selected items come first (most-recently-selected first),
 * followed by unselected items in their original order.
 *
 * @param list        The full, original-order list of items.
 * @param isSelected  Predicate: is this item currently selected?
 * @param recencyOf   Selection recency for an item (higher = more recently selected).
 *                    Only consulted for selected items.
 */
const sortSelectedFirst = <T>(
  list: T[],
  isSelected: (item: T) => boolean,
  recencyOf: (item: T) => number
): T[] => {
  const selected = list.filter(isSelected).sort((a, b) => recencyOf(b) - recencyOf(a));
  const unselected = list.filter((item) => !isSelected(item));
  return [...selected, ...unselected];
};

/**
 * Expands a composite filter entry into the statuses it actually filters on.
 * OPERATING and ERROR are umbrella entries; every other status maps to itself.
 *
 * @param statusItem - The status shown in the filter list.
 * @returns The statuses that entry represents in the active filters.
 */
export const getUnderlyingStatuses = (statusItem: string): string[] => {
  if (statusItem === SecondaryStatusRoboticMowerEnum.OPERATING) {
    return [
      SecondaryStatusRoboticMowerEnum.MOWING,
      SecondaryStatusRoboticMowerEnum.TRANSPORTING,
      SecondaryStatusRoboticMowerEnum.CHARGING,
      SecondaryStatusRoboticMowerEnum.PREPARING,
    ];
  }
  if (statusItem === SecondaryStatusRoboticMowerEnum.ERROR) {
    return [SecondaryStatusRoboticMowerEnum.ERROR, SecondaryStatusRoboticMowerEnum.FATAL_ERROR];
  }
  return [statusItem];
};

/**
 * Reorders a status filter list so the active ones come first, most recently selected first.
 * Composite entries (OPERATING, ERROR) count as selected when any of their underlying
 * statuses is active.
 *
 * @param list - The full, original-order status list.
 * @param activeFilters - The currently active filter values, in selection order.
 * @returns A new list with the active statuses moved to the front.
 */
export const sortStatusesSelectedFirst = <T extends string>(list: T[], activeFilters?: readonly string[]): T[] =>
  sortSelectedFirst(
    list,
    (item) => getUnderlyingStatuses(item).some((status) => activeFilters?.includes(status) ?? false),
    (item) => Math.max(-1, ...getUnderlyingStatuses(item).map((status) => activeFilters?.indexOf(status) ?? -1))
  );

/**
 * Sorts service contract types according to a predefined priority order.
 * @param types - An array of ServiceContractTypes to be sorted.
 * @returns A new array of ServiceContractTypes sorted according to the defined order.
 */

export const sortServiceContractTypes = (types: ServiceContractTypes[]): ServiceContractTypes[] => {
  const order: ServiceContractTypes[] = [
    ServiceContractTypes.LeasePlus,
    ServiceContractTypes.LeasePlusPro,
    ServiceContractTypes.WarrantyPlus,
    ServiceContractTypes.WarrantyPlusPro,
    ServiceContractTypes.ServicePlus,
    ServiceContractTypes.ServicePlusPro,
    ServiceContractTypes.Insurance,
    ServiceContractTypes.WithoutServices,
  ];
  return types.slice().sort((a, b) => order.indexOf(a) - order.indexOf(b));
};

/**
 * Sorts Services ExpiryStatusList according to a predefined priority order.
 * @param expiryStatusList - An array of ServicesExpiryStatusList to be sorted.
 * @returns A new array of ServicesExpiryStatusList sorted according to the defined order.
 */
export const sortServicesExpiryStatusList = (expiryStatusList: CalculatedContractStatusEnum[]): CalculatedContractStatusEnum[] => {
  const order: CalculatedContractStatusEnum[] = [
    CalculatedContractStatusEnum.ACTIVE,
    CalculatedContractStatusEnum.ABOUT_TO_EXPIRE_90,
    CalculatedContractStatusEnum.ABOUT_TO_EXPIRE_60,
    CalculatedContractStatusEnum.ABOUT_TO_EXPIRE_30,
    CalculatedContractStatusEnum.RECENTLY_EXPIRED,
    CalculatedContractStatusEnum.EXPIRED
  ];
  return expiryStatusList.slice().sort((a, b) => order.indexOf(a) - order.indexOf(b));
};
